import { useEffect, useRef } from 'react'
import { IconeCheque, IconeImprimir } from '../../../components/Icones'
import { EtiquetaDeStatus } from '../../../components/Etiqueta'
import { TERMOS, TEXTOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'
import { LinkDeAcao } from './LinkDeAcao'
import { ROTULO_DO_CHAMADO } from './pendencias'

/**
 * O que aconteceu depois de salvar — e, principalmente, o que NÃO aconteceu.
 *
 * O sistema anterior terminava o cadastro numa faixa verde de "sucesso" e devolvia a pessoa à
 * listagem. Ninguém dizia que a solicitação tinha ficado em rascunho, e a leitura natural de
 * "salvo com sucesso" é "pronto, está com o comitê". Solicitação parada em rascunho é mudança que
 * ou não vai acontecer, ou vai acontecer sem aprovação — e implantar sem aprovação do comitê vale
 * 10 pontos de IAD (Anexo VIII, item 16).
 *
 * O foco vai para o título ao montar: quem navega por teclado ou leitor de tela continuaria com o
 * foco no botão "Salvar", que não existe mais na tela, e não teria como saber que a solicitação
 * foi criada nem qual é o código dela.
 *
 * @param props a solicitação recém-criada, como o servidor a devolveu
 * @returns a tela de conclusão do cadastro
 */
export function ConclusaoDoCadastro({ gmud }: { gmud: DetalheGmud }) {
  const titulo = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    titulo.current?.focus()
  }, [])

  // `pendencias` cobre só o plano — são os seis artefatos de `PlanoDeMudanca.pendencias()`. A
  // invariante de envio (`Gmud.verificarInvariantes`) cobra também o número do chamado, e sem esta
  // linha a conferência da etapa 3 diria que falta o chamado e esta tela, meio segundo depois,
  // diria que não falta nada.
  const faltando = gmud.chamadoNumero ? gmud.pendencias : [...gmud.pendencias, ROTULO_DO_CHAMADO]

  return (
    <div className="space-y-5">
      <header className="cartao p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-800">
            <IconeCheque className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 ref={titulo} tabIndex={-1} className="titulo-da-pagina focus:outline-none">
              Solicitação {gmud.codigo} criada
            </h1>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
              <EtiquetaDeStatus status={gmud.status} rotulo={gmud.statusRotulo} />
              <span>
                Ela ficou gravada como rascunho e ainda <strong className="font-semibold">não foi enviada</strong> ao{' '}
                {TERMOS.cab.rotulo.toLowerCase()}.
              </span>
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <LinkDeAcao to={`/gmuds/${gmud.id}`} aparencia="primario">
            Abrir a solicitação
          </LinkDeAcao>
          <LinkDeAcao to="/gmuds">Ver todas as solicitações</LinkDeAcao>
        </div>
      </header>

      {faltando.length > 0 && (
        <section className="rounded-xl border border-laranja-200 bg-laranja-50 p-4 text-sm text-laranja-900">
          <h2 className="font-semibold">O que ainda falta para enviar ao {TERMOS.cab.rotulo.toLowerCase()}</h2>
          {/* A lista vem do servidor, e não da conferência da etapa 3: é a mesma que a transição
              vai cobrar, palavra por palavra. Traduzi-la aqui abriria espaço para a tela dizer que
              falta uma coisa e o envio recusar por outra. */}
          <ul className="mt-2 list-inside list-disc space-y-1">
            {faltando.map((pendencia) => (
              <li key={pendencia}>{pendencia}</li>
            ))}
          </ul>
          <p className="mt-3">Abra a solicitação para completar o plano quando quiser.</p>
        </section>
      )}

      <section className="cartao border-l-4 border-l-laranja-500 p-5">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <IconeImprimir className="h-5 w-5 text-slate-500" />
          {TEXTOS.relatorio.botao} ({TERMOS.rdm.sigla})
        </h2>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{TEXTOS.relatorio.ajuda}</p>
        <LinkDeAcao to={`/gmuds/${gmud.id}/relatorio`} target="_blank" rel="noreferrer" className="mt-4">
          <IconeImprimir className="h-4 w-4" />
          {TEXTOS.relatorio.botao}
          <span className="sr-only">(abre em outra aba)</span>
        </LinkDeAcao>
      </section>
    </div>
  )
}
